import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
  ArrowRight,
  Check,
  CheckCircle2,
  ChevronRight,
  Circle,
  Compass,
  Map,
  Sparkles,
  Wand2,
} from "lucide-react";
import { Button } from "@repo/ui/primitives/button";
import { Badge } from "@repo/ui/primitives/badge";
import { ScrollArea } from "@repo/ui/primitives/scroll-area";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@repo/ui/primitives/accordion";
import { cn } from "@/lib/utils";
import { SETUP_ROADMAP_PHASES, SETUP_UI } from "@/config/setup-roadmap.config.js";
import { useSetupRoadmapProgress } from "@/hooks/useSetupRoadmapProgress.js";

function ProgressBar({ percent, className }) {
  const clamped = Math.max(0, Math.min(100, percent || 0));
  return (
    <div className={cn("h-1.5 w-full rounded-full overflow-hidden", SETUP_UI.progressTrack, className)}>
      <div
        className={cn("h-full rounded-full transition-all duration-300", SETUP_UI.progressBar)}
        style={{ width: `${clamped}%` }}
      />
    </div>
  );
}

/**
 * Full setup roadmap panel (page or embed).
 * @param {{ onNavigate?: (path: string) => void, className?: string, compact?: boolean, embedded?: boolean }} props
 */
export function SetupRoadmapPanel({ onNavigate, className, compact = false, embedded = false }) {
  const { t, ready } = useTranslation("setupGuide", { useSuspense: false });
  const navigate = useNavigate();
  const [activePhaseId, setActivePhaseId] = useState(SETUP_ROADMAP_PHASES[0].id);
  const [expandedStep, setExpandedStep] = useState(SETUP_ROADMAP_PHASES[0].steps[0]?.id);
  const userPickedPhaseRef = React.useRef(false);
  const {
    isStepDone,
    isStepTracked,
    totalSteps: progressTotal,
    doneSteps: progressDone,
    overallPercent,
    getPhaseProgress,
    resumeTarget,
    probesReady,
  } = useSetupRoadmapProgress();

  const principles = useMemo(() => {
    const raw = t("intro.principles", { returnObjects: true, defaultValue: [] });
    return Array.isArray(raw) ? raw.filter(Boolean) : [];
  }, [t, ready]);

  const activePhase = useMemo(
    () => SETUP_ROADMAP_PHASES.find((p) => p.id === activePhaseId) || SETUP_ROADMAP_PHASES[0],
    [activePhaseId]
  );

  const phaseProgress = getPhaseProgress(activePhase);

  // Open the first incomplete phase/step when the modal loads (setup not finished).
  useEffect(() => {
    if (!ready || !probesReady || userPickedPhaseRef.current) return;
    if (!resumeTarget?.phaseId) return;
    setActivePhaseId(resumeTarget.phaseId);
    setExpandedStep(resumeTarget.stepId);
  }, [ready, probesReady, resumeTarget]);

  const selectPhase = (phaseId) => {
    userPickedPhaseRef.current = true;
    setActivePhaseId(phaseId);
    const phase = SETUP_ROADMAP_PHASES.find((p) => p.id === phaseId) || SETUP_ROADMAP_PHASES[0];
    const incomplete = phase.steps.find(
      (step) => isStepTracked(phase.id, step.id) && !isStepDone(phase.id, step.id)
    );
    setExpandedStep(incomplete?.id || phase.steps[0]?.id);
  };

  const handleNavigate = (path) => {
    if (!path) return;
    if (onNavigate) onNavigate(path);
    else navigate(path);
  };

  const roadmapStepCount = SETUP_ROADMAP_PHASES.reduce((n, p) => n + p.steps.length, 0);
  const scrollMax = compact ? "max-h-[calc(92vh-11rem)]" : "max-h-[calc(100vh-12rem)]";

  if (!ready) {
    return (
      <div className={cn("rounded-2xl border border-neutral-200 bg-white p-8 text-sm text-foreground/70", className)}>
        …
      </div>
    );
  }

  return (
    <div
      className={cn(
"flex flex-col overflow-hidden bg-white",
        !embedded && "rounded-2xl border border-neutral-200 shadow-sm",
        className
      )}
    >
      <header className="shrink-0 space-y-3 px-5 pt-5 pb-4 md:px-7 border-b border-neutral-100 bg-white pr-32">
        <div className="flex items-start gap-3 min-w-0">
          <div className={cn("p-2.5 rounded-2xl border shrink-0", SETUP_UI.icon)}>
            <Map className="h-5 w-5 text-current" />
          </div>
          <div className="min-w-0 space-y-1.5 flex-1">
            <h1 className="text-xl md:text-2xl font-semibold tracking-tight text-foreground">
              {t("title", { defaultValue: "Setup Roadmap" })}
            </h1>
            <p className="text-sm text-foreground/70 leading-relaxed max-w-3xl">
              {t("subtitle", {
                defaultValue:
"Recommended end-to-end order to start using sGRC safely and consistently",
              })}
            </p>
            <div className="flex flex-wrap items-center gap-2 pt-1">
              <Badge className={cn("text-[10px] font-semibold uppercase tracking-wide border", SETUP_UI.metaChip)}>
                {roadmapStepCount} {t("stepLabel", { defaultValue: "Step" })}
              </Badge>
              <Badge className={cn("text-[10px] font-semibold uppercase tracking-wide border", SETUP_UI.metaChip)}>
                {t("progressCount", {
                  done: progressDone,
                  total: progressTotal,
                  defaultValue: `${progressDone} / ${progressTotal} done`,
                })}
              </Badge>
            </div>
          </div>
        </div>
        <div className="space-y-1.5">
          <div className="flex items-center justify-between gap-2 text-[11px] text-foreground/70">
            <span className="font-medium">
              {t("overallProgress", { defaultValue: "Setup progress" })}
            </span>
            <span className="tabular-nums font-medium">{overallPercent}%</span>
          </div>
          <ProgressBar percent={overallPercent} />
        </div>
      </header>

      <div className="flex-1 min-h-0 grid grid-cols-1 md:grid-cols-[240px_minmax(0,1fr)] lg:grid-cols-[280px_minmax(0,1fr)]">
        <aside className="border-b md:border-b-0 md:border-r border-neutral-100 bg-white min-h-0">
          <ScrollArea className={cn("h-[220px] md:h-full", scrollMax)}>
            <div className="p-3 space-y-1.5">
              {SETUP_ROADMAP_PHASES.map((phase, index) => {
                const Icon = phase.icon;
                const isActive = phase.id === activePhaseId;
                const progress = getPhaseProgress(phase);
                return (
                  <button
                    key={phase.id}
                    type="button"
                    onClick={() => selectPhase(phase.id)}
                    className={cn(
"w-full text-left rounded-xl border px-3 py-2.5 transition-all duration-200",
                      isActive
                        ? "bg-neutral-50 border-neutral-200 shadow-sm"
                        : "bg-transparent border-transparent hover:bg-neutral-50 hover:border-neutral-200"
                    )}
                  >
                    <div className="flex items-start gap-2.5">
                      <div
                        className={cn(
"mt-0.5 flex h-7 w-7 items-center justify-center rounded-lg border text-[11px] font-bold shrink-0",
                          progress.complete
                            ? "bg-white text-blue-600 border-neutral-200"
                            : isActive
                              ? SETUP_UI.icon
                              : "bg-white text-foreground/60 border-neutral-200"
                        )}
                      >
                        {progress.complete ? (
                          <Check className="h-3.5 w-3.5" />
                        ) : isActive ? (
                          <Icon className="h-3.5 w-3.5 text-current" />
                        ) : (
                          index + 1
                        )}
                      </div>
                      <div className="min-w-0 flex-1 space-y-1">
                        <p
                          className={cn(
"text-sm font-semibold leading-snug",
                            isActive ? "text-foreground" : "text-foreground/65"
                          )}
                        >
                          {t(`phases.${phase.id}.short`, {
                            defaultValue: t(`phases.${phase.id}.title`),
                          })}
                        </p>
                        <p className="text-[11px] text-foreground/55">
                          {t("stepsInPhase", {
                            count: phase.steps.length,
                            defaultValue: `${phase.steps.length} steps`,
                          })}
                        </p>
                      </div>
                      <ChevronRight
                        className={cn(
"h-4 w-4 mt-1 shrink-0 transition-transform text-cyan-600",
                          isActive && "translate-x-0.5"
                        )}
                      />
                    </div>
                  </button>
                );
              })}
            </div>
          </ScrollArea>
        </aside>

        <section className="min-h-0 flex flex-col bg-white">
          <ScrollArea className={cn("flex-1", scrollMax)}>
            <div className="p-4 md:p-6 space-y-5">
              {activePhaseId === "foundation" && (
                <div className={cn(SETUP_UI.box, "p-4 md:p-5 space-y-3")}>
                  <div className="flex items-start gap-3">
                    <div className={cn("p-2 rounded-xl border shrink-0", SETUP_UI.icon)}>
                      <Compass className="h-4 w-4 text-current" />
                    </div>
                    <div className="space-y-1 min-w-0">
                      <h3 className="text-sm font-semibold text-foreground">
                        {t("intro.title", { defaultValue: "How to use this roadmap" })}
                      </h3>
                      <p className="text-xs text-foreground/75 leading-relaxed">
                        {t("intro.body", { defaultValue: "This roadmap shows the recommended order for building a sound data model in sGRC. Do not jump into advanced modules before finishing the required foundation — each phase feeds the next. Modules outside your license can be skipped. Establish identity, access, and inventory first." })}
                      </p>
                    </div>
                  </div>
                  <ul className="grid gap-2 sm:grid-cols-2">
                    {principles.map((item) => (
                      <li
                        key={item}
                        className="flex gap-2 rounded-xl bg-white border border-neutral-200 px-3 py-2.5 text-[11px] text-foreground/80 leading-relaxed"
                      >
                        <Sparkles className="h-3.5 w-3.5 text-cyan-600 shrink-0 mt-0.5" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                  {activePhase.wizardPath && (
                    <div className="flex flex-col sm:flex-row sm:items-center gap-2 pt-1">
                      <Button
                        size="sm"
                        className="gap-2"
                        onClick={() => handleNavigate(activePhase.wizardPath)}
                      >
                        <Wand2 className="h-4 w-4" />
                        {t("openWizard", { defaultValue: "Open Foundation Setup Wizard" })}
                      </Button>
                      <p className="text-[11px] text-foreground/60">{t("openWizardHint", { defaultValue: "Complete Units → Roles → Users in a single guided flow" })}</p>
                    </div>
                  )}
                </div>
              )}

              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <div className={cn("p-2.5 rounded-xl border shrink-0", SETUP_UI.icon)}>
                    {React.createElement(activePhase.icon, { className: "h-5 w-5 text-current" })}
                  </div>
                  <div className="min-w-0 space-y-1.5 flex-1">
                    <h2 className="text-lg font-semibold text-foreground leading-tight">
                      {t(`phases.${activePhase.id}.title`)}
                    </h2>
                    <p className="text-sm text-foreground/70 leading-relaxed">
                      {t(`phases.${activePhase.id}.desc`)}
                    </p>
                    <div className={cn("flex items-start gap-2 px-3 py-2.5", SETUP_UI.box)}>
                      <CheckCircle2 className="h-4 w-4 shrink-0 mt-0.5 text-cyan-600" />
                      <div className="min-w-0">
                        <p className={cn("text-[10px] font-bold uppercase tracking-wider", SETUP_UI.boxTitle)}>
                          {t("outcomeLabel", { defaultValue: "Phase outcome" })}
                        </p>
                        <p className="text-xs text-foreground leading-relaxed mt-0.5">
                          {t(`phases.${activePhase.id}.outcome`)}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="space-y-1.5">
                  {phaseProgress.tracked ? (
                    <>
                      <div className="flex items-center justify-between gap-2 text-[11px] text-foreground/70">
                        <span className="font-medium">
                          {t("phaseProgress", { defaultValue: "Phase progress" })}
                        </span>
                        <span className="tabular-nums font-medium">
                          {phaseProgress.done}/{phaseProgress.total} · {phaseProgress.percent}%
                        </span>
                      </div>
                      <ProgressBar percent={phaseProgress.percent} />
                    </>
                  ) : null}
                </div>
              </div>

              <Accordion
                type="single"
                collapsible
                value={expandedStep}
                onValueChange={setExpandedStep}
                className="space-y-2"
              >
                {activePhase.steps.map((step, stepIndex) => {
                  const StepIcon = step.icon;
                  const tracked = isStepTracked(activePhase.id, step.id);
                  const done = tracked && isStepDone(activePhase.id, step.id);
                  return (
                    <AccordionItem
                      key={step.id}
                      value={step.id}
                      className="rounded-2xl px-4 bg-white border border-neutral-200 data-[state=open]:shadow-sm data-[state=open]:border-neutral-300"
                    >
                      <AccordionTrigger className="hover:no-underline py-3.5 gap-3 [&>svg]:text-cyan-600">
                        <div className="flex items-start gap-3 text-left min-w-0 flex-1">
                          {tracked && (
                            <div
                              className={cn(
"mt-0.5 flex h-8 w-8 items-center justify-center rounded-full border shrink-0",
                                done
                                  ? "bg-blue-500 border-blue-500 text-white"
                                  : "bg-white border-neutral-300 text-foreground/35"
                              )}
                              aria-hidden
                            >
                              {done ? <Check className="h-4 w-4" /> : <Circle className="h-4 w-4" />}
                            </div>
                          )}
                          <div className={cn("p-2 rounded-xl border shrink-0", SETUP_UI.icon)}>
                            <StepIcon className="h-4 w-4 text-current" />
                          </div>
                          <div className="min-w-0 space-y-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span
                                className={cn(
"text-[9px] font-bold tracking-wider px-2 py-0.5 rounded-md border uppercase",
                                  SETUP_UI.metaChip
                                )}
                              >
                                {t("stepLabel", { defaultValue: "Step" })} {stepIndex + 1}
                              </span>
                              {done && (
                                <span className={cn("text-[9px] font-bold tracking-wider px-2 py-0.5 rounded-md border uppercase", SETUP_UI.metaChip)}>
                                  {t("completed", { defaultValue: "Done" })}
                                </span>
                              )}
                            </div>
                            <p className="text-sm font-semibold text-foreground">
                              {t(`phases.${activePhase.id}.steps.${step.id}.title`)}
                            </p>
                            <p className="text-xs text-foreground/65 leading-relaxed line-clamp-2">
                              {t(`phases.${activePhase.id}.steps.${step.id}.summary`)}
                            </p>
                          </div>
                        </div>
                      </AccordionTrigger>
                      <AccordionContent className="pb-4">
                        <div className={cn("space-y-4", tracked ? "pl-0 sm:pl-[4.75rem]" : "pl-0 sm:pl-12")}>
                          <div className={cn("p-3 space-y-1.5", SETUP_UI.box)}>
                            <p className={cn("text-[10px] font-bold uppercase tracking-wider", SETUP_UI.boxTitle)}>
                              {t("whyLabel", { defaultValue: "Why it matters" })}
                            </p>
                            <p className="text-xs text-foreground leading-relaxed">
                              {t(`phases.${activePhase.id}.steps.${step.id}.why`)}
                            </p>
                          </div>

                          <div className="flex flex-wrap items-center justify-end gap-2">
                            <Button
                              size="sm"
                              className="gap-2"
                              onClick={() => handleNavigate(step.path)}
                            >
                              {t("openStep", { defaultValue: "Open page" })}
                              <ArrowRight className="h-3.5 w-3.5" />
                            </Button>
                          </div>
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  );
                })}
              </Accordion>

              <div className="rounded-xl border border-dashed border-neutral-200 bg-white px-4 py-3 flex flex-col sm:flex-row sm:items-center gap-3 justify-between">
                <p className="text-[11px] text-foreground/70 leading-relaxed">{t("footerNote", { defaultValue: "Steps complete automatically when you add records on each page — the progress bar updates accordingly." })}</p>
                <Button
                  variant="outline"
                  size="sm"
                  className="gap-2 shrink-0 border-neutral-200 text-foreground/80 hover:bg-neutral-50"
                  onClick={() => handleNavigate("/user-manual/about-sgrc")}
                >
                  {t("openAbout", { defaultValue: "Open About sGRC guide" })}
                  <ArrowRight className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
          </ScrollArea>
        </section>
      </div>
    </div>
  );
}
